import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { MatchStatus } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { MATCH_STATUS_ORDER } from '../messages/index.ts'
import { parseIsoDay, parseOneOf, parsePage, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

// Every fixture across gironi, with its video. Period, ground and club are search params, so a
// filtered view is a link: the campo and the società cards point here with theirs.

export interface MatchOverviewSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  /** ISO days, both inclusive; `null` is the default period around today. */
  readonly from: string | null
  readonly to: string | null
  readonly pitch: number | null
  readonly club: number | null
  readonly status: MatchStatus | null
  readonly recording: RecordingFilter | null
}

/** Whether the fixture has a recording session: the URL's words for the API's boolean. */
export type RecordingFilter = 'with' | 'without'

export const RECORDING_FILTERS: readonly RecordingFilter[] = ['with', 'without']

export function parseMatchStatus(value: unknown): MatchStatus | null {
  return parseOneOf(MATCH_STATUS_ORDER, value)
}

export function parseRecordingFilter(value: unknown): RecordingFilter | null {
  return parseOneOf(RECORDING_FILTERS, value)
}

export const DEFAULT_MATCH_OVERVIEW_SEARCH: MatchOverviewSearch = {
  page: 1,
  from: null,
  to: null,
  pitch: null,
  club: null,
  status: null,
  recording: null,
}

export const matchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matches',
  validateSearch: (search: Record<string, unknown>): MatchOverviewSearch => ({
    page: parsePage(search.page),
    from: parseIsoDay(search.from),
    to: parseIsoDay(search.to),
    pitch: parsePositiveInt(search.pitch),
    club: parsePositiveInt(search.club),
    status: parseMatchStatus(search.status),
    recording: parseRecordingFilter(search.recording),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_MATCH_OVERVIEW_SEARCH)],
  },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/matches.tsx')),
})

/** The list narrowed to one campo, as the campo's card links it. */
export function pitchMatchesLink(pitchId: number) {
  return {
    to: matchesRoute.to,
    search: { ...DEFAULT_MATCH_OVERVIEW_SEARCH, pitch: pitchId },
  } as const
}

/** The list narrowed to one società, either side. */
export function clubMatchesLink(clubId: number) {
  return {
    to: matchesRoute.to,
    search: { ...DEFAULT_MATCH_OVERVIEW_SEARCH, club: clubId },
  } as const
}
